/**
 * Configuration file for the React frontend
 *
 * API URL resolution order:
 * 1. REACT_APP_API_URL env var (if baked in at build time)
 * 2. Runtime detection from current hostname (Railway production)
 * 3. Fallback to localhost for local development
 */

function getApiUrl() {
  // 1. Build-time env var (if available)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2. Runtime detection for Railway (replace "frontend" with "backend" in hostname)
  if (typeof window !== 'undefined' && window.location.hostname.includes('appfrontend')) {
    return window.location.origin.replace('appfrontend', 'appbackend');
  }

  // 3. Local development fallback
  return 'http://localhost:8000';
}

export const API_URL = getApiUrl();

/**
 * The axios `timeout` for a request that holds a dialog or a form open. A
 * dialog whose own request is in flight can't be dismissed (<Modal busy>: its
 * Cancel is disabled, Escape and the scrim are refused), and a form waiting on
 * its request takes no typing and refuses another submit (utils/busyFormProps:
 * the login, register and password-reset forms), so a request with no deadline
 * turns a stalled connection into a screen with no exit short of a reload.
 * With this, a stall ends as a failure with no `response`, and the dialog or
 * form lets go: it shows its message for that, or (Classrooms, whose create
 * can't safely be repeated) closes and leaves the page to say what is known.
 * Every request sent from a busy dialog or form takes it, or a longer deadline
 * of its own (FORGOT_PASSWORD_TIMEOUT_MS, below).
 *
 * It limits each ATTEMPT, not the whole request. A server that never answers
 * is one attempt, so the dialog lets go after this long: a timeout is not
 * retried, because the XHR adapter reports it as ECONNABORTED and axios-retry
 * leaves that code alone. (ETIMEDOUT it does retry, and that is what axios's
 * `clarifyTimeoutError` option and its fetch adapter report: keep to the
 * defaults, or exclude that code in App.js.)
 *
 * What App.js's axios-retry policy does send again, three times, each attempt
 * with this limit: a dropped connection (any method), a 408 / 429 / 502 / 503 /
 * 504 (any method), and any other 5xx on a GET, HEAD, OPTIONS, PUT or DELETE
 * (the methods axios-retry counts as safe to repeat). So the far worst case
 * for one request is about four times this plus the back-off, and a dialog
 * that waits on two requests in a row (the exam start: a probe, then the POST)
 * can wait that long twice. A 401 waits on the session refresh (index.js)
 * before its request is sent again. A request marked SEND_ONCE (below) is
 * never sent again by that policy.
 */
export const HELD_REQUEST_TIMEOUT_MS = 20000;

/**
 * The deadline for the reset-link request (Login's forgot-password form),
 * longer than HELD_REQUEST_TIMEOUT_MS. The server now answers before it sends
 * the email (backend routes/auth.py forgot_password: the send is a background
 * task), so a healthy answer is as quick as a login's. But a backend still on
 * the older inline send (this frontend deployed ahead of it, or a rollback)
 * answers only after the SMTP exchange, each of whose socket operations may
 * take SMTP_TIMEOUT_SECONDS (20 s, backend config.py). A slow relay that still
 * delivers then outlives 20 s, and a visitor told at 20 s that the request
 * wasn't confirmed asks again, minting a new link that voids the email
 * arriving a moment later. Past this deadline the form says it couldn't
 * confirm the email was sent, and to check the inbox before asking again.
 */
export const FORGOT_PASSWORD_TIMEOUT_MS = 60000;

/**
 * Spread into an axios request config to switch off App.js's automatic
 * re-sends for that one request:
 *
 *   axios.post(url, body, { timeout: HELD_REQUEST_TIMEOUT_MS, ...SEND_ONCE })
 *
 * For a write the server can't recognise as a repeat. The policy re-sends any
 * request whose connection dropped, and a connection can drop AFTER the server
 * did the work, on the answer's way back. (Across origins the same goes for
 * whatever is answered outside the API's CORS layer: a crash, the default rate
 * limit, a proxy's error. The browser withholds a response with no CORS
 * headers, so axios sees a dropped connection.) Sent again, such a write
 * creates a second classroom, or spends a one-time code that had just been
 * accepted and comes home refused. Marked with this, it fails once, with no
 * `response`, and its caller decides what is safe to say and do.
 *
 * It switches off that policy and nothing else. The 401 refresh and the 426
 * age gate still send a request again themselves, each after the server
 * refused it before doing any of its work. config.test.js holds the switch
 * against the real axios-retry.
 */
export const SEND_ONCE = Object.freeze({ 'axios-retry': Object.freeze({ retries: 0 }) });

/**
 * Spread into the axios config of a request that carries its own credential,
 * or none, rather than the stored session: a sign-in or registration, a reset
 * token, a verification token, a reset-link request.
 *
 *   axios.post(url, body, { timeout: HELD_REQUEST_TIMEOUT_MS, ...SEND_ONCE, ...OWN_CREDENTIAL })
 *
 * This request's 401 is about the credential it carries, never the stored
 * session: no refresh may act on it, and no sign-out. (A wrong password, a
 * spent or expired link. setupAxiosInterceptor's carriesOwnCredential is the
 * one test for both.) The error still reaches the caller.
 *
 *   - The session refresh. With a session stored (signed in in another tab),
 *     a wrong password or a refused reset was answered by refreshing that
 *     session and sending the request again, presenting the refused
 *     credential twice, spending a second rate-limit slot and rotating the
 *     session for nothing. SEND_ONCE can't stop that: it switches off only
 *     App.js's re-send policy, not the refresh's own re-send.
 *   - App.js's sign-out on a 401, which revoked and removed that other tab's
 *     session.
 *
 * Never on a request whose authority is the stored session's bearer (a poll,
 * a save): its 401 does mean the session is over, and marked with this it
 * would neither refresh nor sign out, leaving the student in a signed-in
 * shell where nothing loads.
 */
export const OWN_CREDENTIAL = Object.freeze({ ownCredential: true });

export const config = {
  apiUrl: API_URL,
};
